import { useState } from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui-shim";
import type { OptionGroup } from "@/lib/item-options";

export const Swatch = ({ hex, name }: { hex?: string; name?: string }) => {
  const multi = name?.toLowerCase() === "multicolor";
  if (!hex && !multi) return null;
  return (
    <span className="inline-block w-3 h-3 rounded-full border border-foreground/20 shrink-0"
      style={multi ? { background: "conic-gradient(#d0021b,#ffd700,#228b22,#1f4fd1,#6a0dad,#d0021b)" } : { background: hex }} />
  );
};

interface ListProps {
  groups: OptionGroup[];
  selected: string[];
  onPick: (v: string) => void;
  allowCustom?: boolean;
  testid: string;
  placeholder?: string;
}

function OptionList({ groups, selected, onPick, allowCustom, testid, placeholder }: ListProps) {
  const [query, setQuery] = useState("");
  const q = query.trim().replace(/\s+/g, " ");
  const all = groups.flatMap((g) => g.options.map((o) => o.value.toLowerCase()));
  const canAdd = allowCustom && q && !all.includes(q.toLowerCase())
    && !selected.some((s) => s.toLowerCase() === q.toLowerCase());
  const pick = (v: string) => { onPick(v); setQuery(""); };
  return (
    <Command>
      <CommandInput value={query} onValueChange={setQuery} placeholder={placeholder || "Search…"}
        data-testid={`${testid}-search`} />
      <CommandList className="max-h-72">
        <CommandEmpty>{allowCustom ? "Type to add a custom value." : "No matches."}</CommandEmpty>
        {groups.filter((g) => g.options.length).map((g) => (
          <CommandGroup key={g.group || "_"} heading={g.group || undefined}>
            {g.options.map((o) => {
              const on = selected.some((s) => s.toLowerCase() === o.value.toLowerCase());
              return (
                <CommandItem key={`${g.group}-${o.value}`} value={`${g.group} ${o.value}`}
                  onSelect={() => pick(o.value)} data-testid={`${testid}-option-${o.value}`}>
                  <span className="w-4 mr-1.5">{on && <Check className="w-3.5 h-3.5 text-oxblood" />}</span>
                  <Swatch hex={o.hex} name={o.value} />
                  <span className={o.hex || o.value === "Multicolor" ? "ml-2" : ""}>{o.value}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
        {canAdd && (
          <CommandGroup heading="Custom">
            <CommandItem value={`__add__ ${q}`} onSelect={() => pick(q)} data-testid={`${testid}-add-custom`}>
              <Plus className="w-3.5 h-3.5 mr-2" /> Add “{q}”
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

const triggerCls = "h-10 w-full border border-border bg-background px-3 text-sm flex items-center gap-2 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-oxblood";

export function SearchSelect({ value, onChange, groups, placeholder = "Select…", testid, allowCustom, hexOf }: {
  value: string | null | undefined; onChange: (v: string | null) => void; groups: OptionGroup[];
  placeholder?: string; testid: string; allowCustom?: boolean; hexOf?: (v: string) => string | undefined;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button type="button" className={triggerCls} data-testid={testid} aria-expanded={open}>
            {value ? <><Swatch hex={hexOf?.(value)} name={value} /><span className="truncate flex-1">{value}</span></>
              : <span className="text-muted-foreground flex-1">{placeholder}</span>}
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        {value && (
          <button type="button" aria-label="Clear" data-testid={`${testid}-clear`}
            onClick={() => onChange(null)}
            className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-oxblood">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px] rounded-none" align="start">
        <OptionList groups={groups} selected={value ? [value] : []} allowCustom={allowCustom} testid={testid}
          onPick={(v) => { onChange(v); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}

export function MultiSelect({ values, onChange, groups, placeholder = "Add…", testid, allowCustom, hexOf, renderChipExtra }: {
  values: string[]; onChange: (v: string[]) => void; groups: OptionGroup[]; placeholder?: string;
  testid: string; allowCustom?: boolean; hexOf?: (v: string) => string | undefined;
  renderChipExtra?: (v: string) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) => {
    const exists = values.some((x) => x.toLowerCase() === v.toLowerCase());
    onChange(exists ? values.filter((x) => x.toLowerCase() !== v.toLowerCase()) : [...values, v]);
  };
  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {values.map((v) => (
            <span key={v} className="inline-flex items-center gap-1.5 border border-border bg-bone/50 pl-2 pr-1 py-0.5 text-xs"
              data-testid={`${testid}-chip-${v}`}>
              <Swatch hex={hexOf?.(v)} name={v} />{v}{renderChipExtra?.(v)}
              <button type="button" aria-label={`Remove ${v}`} onClick={() => toggle(v)}
                className="p-0.5 text-muted-foreground hover:text-oxblood"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={triggerCls} data-testid={testid} aria-expanded={open}>
            <span className="text-muted-foreground flex-1">{placeholder}</span>
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px] rounded-none" align="start">
          <OptionList groups={groups} selected={values} allowCustom={allowCustom} testid={testid} onPick={toggle} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
